<app-page-shell [breadcrumb]="breadcrumb" width="wide">
  <header class="mx-auto max-w-3xl pt-12 pb-14 text-center md:pt-16 md:pb-20">
    <h1 class="hero-title font-display text-4xl font-bold leading-[1.06] text-foreground md:text-[3.25rem]">
      <span>{{ 'pages.dataStories.hero.titlePrefix' | transloco }}</span>
      <span class="hero-title-accent italic font-medium text-primary">{{ 'pages.dataStories.hero.titleAccent' | transloco }}</span>
    </h1>
    <p class="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {{ 'pages.dataStories.hero.description' | transloco }}
    </p>
  </header>

  <!-- Busca -->
  <div
    class="mx-auto mb-12 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 transition focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/30"
  >
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 text-muted-foreground">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
    <input
      #searchInput
      type="text"
      role="searchbox"
      [value]="query()"
      (input)="onSearch(searchInput.value)"
      [attr.placeholder]="'pages.dataStories.searchPlaceholder' | transloco"
      [attr.aria-label]="'pages.dataStories.searchPlaceholder' | transloco"
      class="input-unstyled min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-foreground shadow-none outline-none ring-0 placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none"
    />
    @if (query()) {
      <button
        type="button"
        (click)="onSearch(''); searchInput.value = ''"
        [attr.aria-label]="'pages.dataStories.clearSearch' | transloco"
        class="rounded-full p-1 text-muted-foreground transition hover:text-foreground"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
          <path d="M18 6 6 18" /><path d="m6 6 12 12" />
        </svg>
      </button>
    }
  </div>

  <!-- Filtro por tags -->
  @if (tagFilters().length > 0) {
    <div class="mx-auto mb-12 max-w-4xl">
      <div class="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          (click)="clearTags()"
          [attr.aria-pressed]="activeTags().length === 0"
          class="rounded-full border px-3 py-1 text-xs font-medium transition"
          [class.border-primary]="activeTags().length === 0"
          [class.bg-primary]="activeTags().length === 0"
          [class.text-primary-foreground]="activeTags().length === 0"
          [class.border-border]="activeTags().length > 0"
          [class.bg-card]="activeTags().length > 0"
          [class.text-muted-foreground]="activeTags().length > 0"
        >
          {{ 'pages.dataStories.allTags' | transloco }}
        </button>
        @for (tag of tagFilters(); track tag.name) {
          <button
            type="button"
            (click)="toggleTag(tag.name)"
            [attr.aria-pressed]="activeTags().includes(tag.name)"
            class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition"
            [class.border-primary]="activeTags().includes(tag.name)"
            [class.bg-primary]="activeTags().includes(tag.name)"
            [class.text-primary-foreground]="activeTags().includes(tag.name)"
            [class.border-border]="!activeTags().includes(tag.name)"
            [class.bg-card]="!activeTags().includes(tag.name)"
            [class.text-muted-foreground]="!activeTags().includes(tag.name)"
          >
            {{ tag.name }}
            <span class="text-[10px] tabular-nums opacity-70">{{ tag.count }}</span>
          </button>
        }
      </div>
    </div>
  }

  @if (loading()) {
    <div class="mx-auto max-w-6xl space-y-6">
      <div class="h-48 animate-pulse rounded-3xl bg-primary/[0.06]"></div>
      @for (i of [1, 2, 3]; track i) {
        <div class="h-24 animate-pulse border-t border-border"></div>
      }
    </div>
  } @else if (filtered().length === 0) {
    <div class="mt-16 py-16 text-center">
      <p class="font-display text-lg font-semibold text-foreground">
        {{ 'pages.dataStories.emptyTitle' | transloco }}
      </p>
      <p class="mt-2 text-sm text-muted-foreground">
        {{ 'pages.dataStories.emptyDescription' | transloco }}
        @if (isFiltering()) {
          <button
            type="button"
            (click)="clearFilters()"
            class="text-primary underline-offset-4 hover:underline"
          >
            {{ 'pages.dataStories.clearFilters' | transloco }}
          </button>
        }
      </p>
    </div>
  } @else {
    <!-- Lead -->
    @if (lead() && !isFiltering()) {
      <article class="-mx-6 rounded-3xl bg-primary/[0.06] px-8 py-12 md:-mx-10 md:px-12 md:py-14">
        <div class="grid gap-x-10 gap-y-8 md:grid-cols-12">
          <aside class="md:col-span-3">
            <p class="eyebrow">{{ 'pages.dataStories.leadEyebrow' | transloco }}</p>
            <div class="mt-5 space-y-1.5 text-xs leading-relaxed text-muted-foreground">
              <p class="tabular-nums">{{ lead()!.dateLabel }}</p>
              <p>{{ 'pages.dataStories.authorship' | transloco }}: {{ lead()!.author }}</p>
              <p class="inline-flex items-center gap-1.5 tabular-nums">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                {{ lead()!.viewsLabel }}
              </p>
            </div>
          </aside>

          <div class="md:col-span-9">
            <a
              [routerLink]="lead()!.link | localizedRouterLink"
              class="group block focus-visible:outline-none"
            >
              <h2 class="font-display text-3xl font-semibold leading-[1.1] tracking-[-0.02em] text-foreground transition-colors group-hover:text-secondary md:text-4xl">
                {{ lead()!.title }}
              </h2>
              @if (lead()!.dek) {
                <p class="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-[17px]">
                  {{ lead()!.dek }}
                </p>
              }
              <span class="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-secondary transition-colors group-hover:text-secondary/80">
                {{ 'pages.dataStories.readFullStory' | transloco }}
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4 transition-transform group-hover:translate-x-1">
                  <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                </svg>
              </span>
            </a>
          </div>
        </div>
      </article>
    }

    <!-- Índice -->
    <section aria-labelledby="datastories-index" class="mt-16">
      <header class="mb-2 flex flex-wrap items-end justify-between gap-x-8 gap-y-3 border-b border-foreground/[0.10] pb-4">
        <div>
          <h2 id="datastories-index" class="eyebrow">
            {{ (isFiltering() ? 'pages.dataStories.resultsHeading' : 'pages.dataStories.moreStoriesHeading') | transloco }}
          </h2>
          <p class="mt-1 text-sm text-muted-foreground tabular-nums">
            {{ listItems().length }}
            {{
              (listItems().length === 1
                ? 'pages.dataStories.storySingular'
                : 'pages.dataStories.storyPlural'
              ) | transloco
            }}
          </p>
        </div>

        <div class="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{{ 'pages.dataStories.sortLabel' | transloco }}</span>
          <app-editorial-select
            [options]="sortOptions()"
            [value]="sort()"
            [ariaLabel]="'pages.dataStories.sortLabel' | transloco"
            align="right"
            (valueChange)="onSort($event)"
          />
        </div>
      </header>

      <ol class="divide-y divide-foreground/[0.10]">
        @for (story of listItems(); track story.id) {
          <li>
            <a
              [routerLink]="story.link | localizedRouterLink"
              class="group grid grid-cols-12 items-start gap-x-10 gap-y-3 py-8 transition focus-visible:outline-none"
            >
              <div class="col-span-12 space-y-2 md:col-span-3">
                <p class="text-[13px] leading-relaxed text-muted-foreground tabular-nums">
                  {{ story.dateLabel }}
                </p>
                <p class="text-[13px] leading-relaxed text-muted-foreground">
                  {{ 'pages.dataStories.authorship' | transloco }}: {{ story.author }}
                </p>
                <p class="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground tabular-nums">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
                    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  {{ story.viewsLabel }}
                </p>
              </div>

              <div class="col-span-12 space-y-3 md:col-span-9">
                <h3 class="font-display text-xl font-bold leading-[1.2] tracking-[-0.015em] text-foreground transition-colors group-hover:text-secondary md:text-[22px]">
                  {{ story.title }}
                </h3>
                @if (story.dek) {
                  <p class="text-[15px] leading-[1.65] text-muted-foreground">
                    {{ story.dek }}
                  </p>
                }
              </div>
            </a>
          </li>
        }
      </ol>
    </section>
  }

  <app-promo-banner
    eyebrowKey="pages.dataStories.promo.eyebrow"
    titlePrefixKey="pages.dataStories.promo.titlePrefix"
    titleAccentKey="pages.dataStories.promo.titleAccent"
    descriptionKey="pages.dataStories.promo.description"
    ctaLabelKey="pages.dataStories.promo.cta"
    ctaPath="/nuvem-perguntas"
  />
</app-page-shell>
